// Step 4 of the import wizard: Review and fix (release 3.3, import redesign
// I4; plan 4.5). The rows as they will land, as a grid: one column per
// imported field, one row per vendor, 36 px rows, 50 rows a page from the
// server, the rows with problems first. A problem is said in one short
// sentence with its fix beside it; any cell is edited in place (Enter keeps
// it, Escape puts it back); a value wrong in many rows is fixed once in the
// bulk-fix bar. The rules are plain functions in reviewGrid.ts.
//
// Every fix goes through the shell's handlers (fixRow, holdRow, fixAll, and
// find and replace), which write it beside the file on the server, so a fix
// survives Back, a refresh and another tab, and the counts move at once.

import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { ImportError } from '../../components/ImportUndo'
import { Chip } from '../../components/StatusChip'
import { Skeleton } from '../../components/ui'
import type {
  ImportAnalysis,
  ImportColumnChoices,
  ImportCounts,
  ImportDuplicates,
  ImportFindReplace,
  ImportFindReplaceResult,
  ImportPreview,
  ImportPreviewFilter,
  ImportPreviewRow,
  ImportProblem,
} from '../../lib/api'
import { errorWords, lowerFirst, outcomeWord, plural } from '../../lib/importWords'
import { DRAFT_SAVED } from './draftSync'
import {
  cellView,
  DUPLICATE_RULES,
  duplicateAnswers,
  fileValueFor,
  filterTiles,
  firstSentence,
  fixKind,
  footerWords,
  gridColumns,
  likelyOptions,
  NOTHING_SAVED,
  pageList,
  rangeWords,
  REVIEW_PAGE,
  type GridColumn,
} from './reviewGrid'
import '../../styles/home.css'
import './review.css'

// The key a fix is sent under: the file column to edit, or, for a problem
// with no column (a choice for the field itself), "@" + the field. Whatever
// the field is: "@constitution" for a legal form, "@match" for whether a row
// is a vendor already on the register.
export function fixKey(problem: Pick<ImportProblem, 'field' | 'column'>): string {
  return problem.column !== '' ? problem.column : '@' + problem.field
}

type FixFn = (row: ImportPreviewRow, key: string, value: string) => Promise<void>
type HoldFn = (row: ImportPreviewRow, hold: boolean) => Promise<void>

// A fix all the bar offers after a cell was fixed by hand: the same change in
// every row of that column whose cell says the same.
type Suggestion = { column: string; label: string; from: string; to: string }

// ------------------------------------------------------------ small parts

function SkipGlyph({ held }: { held: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <circle cx="8" cy="8" r="5.8" />
      {held ? <path d="M8 5.2v5.6M5.2 8h5.6" /> : <path d="m3.9 3.9 8.2 8.2" />}
    </svg>
  )
}

// Skip or Include, as an icon button with its tooltip (plan 4.5). Skip is
// reversible: an on-hold row offers Include.
function HoldButton({ row, disabled, onHold, onError }: { row: ImportPreviewRow; disabled: boolean; onHold: HoldFn; onError: (row: ImportPreviewRow, err: unknown) => void }) {
  const [busy, setBusy] = useState(false)
  const held = row.outcome === 'HELD'
  const tip = held ? 'Include this vendor' : 'Skip this vendor'
  return (
    <button
      type="button"
      className="imw-hold imw-icon imw-tip"
      data-tip={tip}
      aria-label={(held ? 'Include' : 'Skip') + ' row ' + row.sourceRow}
      disabled={disabled || busy}
      onClick={async () => {
        setBusy(true)
        try {
          await onHold(row, !held)
        } catch (err) {
          onError(row, err)
        } finally {
          setBusy(false)
        }
      }}
    >
      <SkipGlyph held={held} />
    </button>
  )
}

// The problem column: the row's first problem in one sentence and its fix,
// or, for a row with none, whom it matched on the register.
function ProblemCell({
  row,
  choices,
  inGrid,
  readOnly,
  onFix,
  onHold,
  onEdit,
  onError,
}: {
  row: ImportPreviewRow
  choices: ImportColumnChoices
  inGrid: (column: string) => boolean
  readOnly: boolean
  onFix: FixFn
  onHold: HoldFn
  onEdit: (column: string) => void
  onError: (row: ImportPreviewRow, err: unknown) => void
}) {
  const [busy, setBusy] = useState(false)
  const problems = row.problems ?? []
  const p = problems[0]
  // An answer already given is shown as given (pressed, or chosen in the
  // list), by its index: the code itself is never drawn.
  const given = p ? row.fixes?.[fixKey(p)] : undefined
  const chosen = p && given !== undefined ? (p.options ?? []).findIndex((o) => o.code === given) : -1
  const [pick, setPick] = useState(chosen)
  useEffect(() => setPick(chosen), [p?.field, p?.column, p?.message, chosen])

  if (!p) {
    const m = row.matched
    return (
      <td className="imw-gprob">
        {m ? (
          <span className="imw-gquiet" title={'Already on the register as ' + m.name + (m.how ? ', ' + lowerFirst(m.how) : '')}>
            Matches {m.name}
            {m.how ? ', ' + lowerFirst(m.how) : ''}
          </span>
        ) : null}
      </td>
    )
  }

  async function run(f: () => Promise<void>) {
    setBusy(true)
    try {
      await f()
    } catch (err) {
      onError(row, err)
    } finally {
      setBusy(false)
    }
  }
  const choose = (i: number) => run(() => onFix(row, fixKey(p), p.options?.[i]?.code ?? ''))
  const off = readOnly || busy
  const kind = fixKind(p)
  const opts = p.options ?? []
  const rest = problems.slice(1).map((x) => x.message)
  const where = ', row ' + row.sourceRow
  // A row with a problem still says whom it matched on the register, in the
  // problem's tooltip (a row without one says it in the cell).
  const m = row.matched
  const matchedLine = m ? 'Already on the register as ' + m.name + (m.how ? ', ' + lowerFirst(m.how) : '') : ''

  let fix: ReactNode = null
  if (kind === 'duplicate') {
    fix = (
      <>
        {duplicateAnswers(opts).map((a) => (
          <button
            key={a.index}
            type="button"
            className="imw-gbtn"
            title={a.title}
            aria-pressed={a.index === chosen}
            disabled={off}
            onClick={() => void choose(a.index)}
          >
            {a.label}
            <span className="vh">{where}</span>
          </button>
        ))}
        {row.outcome !== 'HELD' && (
          <button type="button" className="imw-gbtn" disabled={off} onClick={() => void run(() => onHold(row, true))}>
            Skip<span className="vh">{where}</span>
          </button>
        )}
      </>
    )
  } else if (kind === 'choice') {
    const few = opts.length <= 3
    const likely = few ? opts.map((_, i) => i) : likelyOptions(fileValueFor(row, p, choices), opts)
    const sid = 'imw-gpick-' + row.sourceRow
    fix = (
      <>
        {likely.map((i) => (
          <button
            key={i}
            type="button"
            className="imw-gbtn"
            disabled={off}
            title={'Use ' + opts[i]!.label}
            aria-pressed={i === chosen}
            onClick={() => void choose(i)}
          >
            Use {opts[i]!.label}
            <span className="vh">{where}</span>
          </button>
        ))}
        {!few && (
          <span className="imw-gpick">
            <label className="vh" htmlFor={sid}>
              {'Choose ' + p.label + where}
            </label>
            <select id={sid} value={pick >= 0 ? String(pick) : ''} disabled={off} onChange={(e) => setPick(Number(e.target.value))}>
              <option value="">{likely.length > 0 ? 'Other' : 'Choose'}</option>
              {opts.map((o, i) => (
                <option key={i} value={String(i)}>
                  {o.label}
                </option>
              ))}
            </select>
            <button type="button" className="imw-gbtn" disabled={off || pick < 0} onClick={() => void choose(pick)}>
              Use<span className="vh"> the chosen {p.label.toLowerCase() + where}</span>
            </button>
          </span>
        )}
      </>
    )
  } else if (p.column !== '' && inGrid(p.column)) {
    fix = (
      <button type="button" className="imw-gbtn" disabled={off} onClick={() => onEdit(p.column)}>
        Edit<span className="vh"> {p.label + where}</span>
      </button>
    )
  }

  return (
    <td className="imw-gprob" aria-busy={busy || undefined}>
      <span className="imw-gmsg" title={[p.message, ...rest, matchedLine].filter(Boolean).join('\n')}>
        {firstSentence(p.message)}
      </span>
      {fix}
      {rest.length > 0 && (
        <span className="imw-gmore" title={rest.join('\n')}>
          +{rest.length} more
        </span>
      )}
    </td>
  )
}

// One cell of the grid: the value as it will land, or, while it is edited, a
// text box with what the file says. Enter keeps the change, Escape puts it
// back, leaving the box keeps it too (as a spreadsheet does).
function GridCell({
  row,
  col,
  r,
  c,
  active,
  editing,
  readOnly,
  onStart,
  onCommit,
  onCancel,
  onMove,
}: {
  row: ImportPreviewRow
  col: GridColumn
  r: number
  c: number
  active: boolean
  editing: boolean
  readOnly: boolean
  onStart: () => void
  onCommit: (value: string) => void
  onCancel: () => void
  onMove: (dr: number, dc: number, e: KeyboardEvent) => void
}) {
  const v = cellView(row, col)
  const [text, setText] = useState(v.file)
  const done = useRef(false)
  useEffect(() => {
    if (editing) {
      setText(v.file)
      done.current = false
    }
    // Only when the editing starts: the file's value is what the box opens on.
  }, [editing])

  const problem = v.problems.length > 0
  const cls = 'imw-gcell' + (problem ? ' imw-gbad' : '') + (col.name ? ' imw-gname' : '')
  if (editing) {
    const finish = (keep: boolean) => {
      if (done.current) return
      done.current = true
      if (keep && text !== v.file) onCommit(text)
      else onCancel()
    }
    return (
      <td className={cls + ' imw-gedit'}>
        <input
          type="text"
          autoFocus
          aria-label={'New value for ' + col.label + ', row ' + row.sourceRow}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              finish(true)
            } else if (e.key === 'Escape') {
              e.preventDefault()
              e.stopPropagation()
              finish(false)
            }
          }}
          onBlur={() => finish(true)}
        />
      </td>
    )
  }
  return (
    <td className={cls}>
      <button
        type="button"
        data-cell={r + ':' + c}
        tabIndex={active ? 0 : -1}
        title={v.title || undefined}
        aria-label={col.label + ', row ' + row.sourceRow + ': ' + (v.text || 'empty') + (problem ? '. ' + v.problems.map((p) => p.message).join(' ') : '')}
        // Read-only, the cell still takes the grid's one tab stop and the
        // arrows, so the values can be read from the keyboard; it opens no
        // box. Disabled, it took the grid out of the tab order (I4 review).
        aria-disabled={readOnly || undefined}
        onClick={readOnly ? undefined : onStart}
        onKeyDown={(e) => {
          const moves: Record<string, [number, number]> = { ArrowRight: [0, 1], ArrowLeft: [0, -1], ArrowDown: [1, 0], ArrowUp: [-1, 0] }
          const m = moves[e.key]
          if (m) onMove(m[0], m[1], e)
          else if (e.key === 'Home') onMove(0, -1000, e)
          else if (e.key === 'End') onMove(0, 1000, e)
          else if (e.key === 'F2') {
            e.preventDefault()
            if (!readOnly) onStart()
          }
        }}
      >
        {problem && <span className="imw-gwarn" aria-hidden="true" />}
        {v.text !== '' ? <span className="imw-gtext">{v.text}</span> : <span className="imw-gempty">Empty</span>}
        {v.fixed && <span className="imw-dot" aria-hidden="true" />}
      </button>
    </td>
  )
}

// ---------------------------------------------------------- bulk fix bar

function BulkFix({
  columns,
  suggest,
  readOnly,
  onDismiss,
  onFixAll,
  onFindReplace,
}: {
  columns: GridColumn[]
  suggest: Suggestion | null
  readOnly: boolean
  onDismiss: () => void
  onFixAll: (column: string, from: string, to: string) => Promise<void>
  onFindReplace: (req: ImportFindReplace) => Promise<ImportFindReplaceResult>
}) {
  const [open, setOpen] = useState(false)
  const [col, setCol] = useState(0)
  const [find, setFind] = useState('')
  const [replace, setReplace] = useState('')
  const [whole, setWhole] = useState(false)
  const [matchCase, setMatchCase] = useState(false)
  const [seen, setSeen] = useState<{ key: string; r: ImportFindReplaceResult } | null>(null)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState<unknown>(null)
  const target = columns[col] ?? columns[0]
  const req = (preview: boolean): ImportFindReplace => ({
    column: target?.column ?? '',
    find,
    replace,
    wholeCell: whole,
    matchCase,
    preview,
  })
  const key = JSON.stringify(req(true))
  const current = seen && seen.key === key ? seen.r : null

  async function go(preview: boolean) {
    setBusy(preview ? 'preview' : 'apply')
    setError(null)
    try {
      const r = await onFindReplace(req(preview))
      if (preview) setSeen({ key, r })
      else {
        setSeen(null)
        setFind('')
        setReplace('')
        setOpen(false)
      }
    } catch (err) {
      setError(err)
    } finally {
      setBusy('')
    }
  }

  async function all() {
    if (!suggest) return
    setBusy('all')
    setError(null)
    try {
      await onFixAll(suggest.column, suggest.from, suggest.to)
      onDismiss()
    } catch (err) {
      setError(err)
    } finally {
      setBusy('')
    }
  }

  return (
    <div className="imw-bulk" role="group" aria-label="Bulk fix">
      <div className="imw-bulk-line">
        <span className="imw-bulk-h">Bulk fix</span>
        {suggest ? (
          <span className="imw-bulk-say">
            <span title={'Change every "' + suggest.from + '" in ' + suggest.label + ' to "' + suggest.to + '"'}>
              Change every "{suggest.from}" in {suggest.label} to "{suggest.to}"
            </span>
            <button type="button" disabled={readOnly || busy !== ''} onClick={() => void all()}>
              {busy === 'all' ? 'Changing...' : 'Apply to all'}
            </button>
            <button type="button" className="linkish" onClick={onDismiss}>
              Not now
            </button>
          </span>
        ) : (
          <span className="imw-muted">Fix a cell, and the same change can go to every row like it.</span>
        )}
        <button type="button" className="imw-bulk-open" aria-expanded={open} disabled={readOnly || columns.length === 0} onClick={() => setOpen(!open)}>
          Find and replace
        </button>
      </div>
      {open && (
        <div className="imw-fr">
          <label>
            In
            <select value={String(col)} onChange={(e) => setCol(Number(e.target.value))}>
              {columns.map((g, i) => (
                <option key={i} value={String(i)}>
                  {g.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Find
            <input type="text" value={find} maxLength={200} onChange={(e) => setFind(e.target.value)} />
          </label>
          <label>
            Replace with
            <input type="text" value={replace} maxLength={200} onChange={(e) => setReplace(e.target.value)} />
          </label>
          <label className="imw-fr-check">
            <input type="checkbox" checked={whole} onChange={(e) => setWhole(e.target.checked)} />
            Whole cell
          </label>
          <label className="imw-fr-check">
            <input type="checkbox" checked={matchCase} onChange={(e) => setMatchCase(e.target.checked)} />
            Match case
          </label>
          <button type="button" disabled={busy !== '' || find === ''} onClick={() => void go(true)}>
            {busy === 'preview' ? 'Looking...' : 'Preview'}
          </button>
          {current && (
            <div className="imw-fr-said" role="status">
              <span>{current.note}</span>
              {current.examples.length > 0 && (
                <ul>
                  {current.examples.slice(0, 3).map((x) => (
                    <li key={x.rowId}>
                      Row {x.sourceRow}: <span title={x.before}>{x.before}</span> to <span title={x.after}>{x.after}</span>
                    </li>
                  ))}
                </ul>
              )}
              {current.changed > 0 && (
                <button type="button" className="primary" disabled={busy !== ''} onClick={() => void go(false)}>
                  {busy === 'apply' ? 'Replacing...' : 'Replace in ' + plural(current.changed, 'row')}
                </button>
              )}
            </div>
          )}
        </div>
      )}
      <ImportError error={error} />
    </div>
  )
}

// ------------------------------------------------------------------ step

export function CheckStep({
  analysis,
  choices,
  counts,
  preview,
  filter,
  duplicates,
  loading,
  busy,
  skippedValues,
  error,
  readOnly = false,
  draftNote = '',
  fixNote = '',
  onFilter,
  onPage,
  onDuplicates,
  onFix,
  onHold,
  onFixAll,
  onFindReplace,
  onBack,
  onNext,
}: {
  analysis: Pick<ImportAnalysis, 'columns' | 'fields'>
  choices: ImportColumnChoices
  counts: ImportCounts
  preview: ImportPreview | null
  filter: ImportPreviewFilter
  duplicates: ImportDuplicates
  loading: boolean
  busy: boolean
  skippedValues: boolean
  error: unknown
  readOnly?: boolean
  // The footer's quiet line about the server draft.
  draftNote?: string
  // One line when columns that carry fixes are matched differently now.
  fixNote?: string
  onFilter: (f: ImportPreviewFilter) => void
  onPage: (offset: number) => void
  onDuplicates: (d: ImportDuplicates) => void
  onFix: FixFn
  onHold: HoldFn
  onFixAll: (column: string, from: string, to: string) => Promise<void>
  onFindReplace: (req: ImportFindReplace) => Promise<ImportFindReplaceResult>
  onBack: () => void
  onNext: () => void
}) {
  const cols = useMemo(() => gridColumns(analysis, choices), [analysis, choices])
  const rows = preview?.rows ?? []
  const offset = preview?.offset ?? 0
  const total = preview?.total ?? 0
  const ready = counts.add + counts.update
  const pages = Math.max(1, Math.ceil(total / REVIEW_PAGE))
  const page = Math.floor(offset / REVIEW_PAGE) + 1

  const grid = useRef<HTMLTableElement>(null)
  const [active, setActive] = useState<[number, number]>([0, 0])
  // The roving cell must be a cell on screen, or no cell takes Tab: a new
  // page starts at the first cell; a filter or column change that leaves the
  // cell off the rows starts there too.
  const lastOffset = useRef(offset)
  useEffect(() => {
    setActive(([r, c]) => {
      if (lastOffset.current !== offset) {
        lastOffset.current = offset
        return [0, 0]
      }
      return r < rows.length && c < cols.length ? [r, c] : [0, 0]
    })
  }, [offset, rows.length, cols.length])
  const [editing, setEditing] = useState<{ rowId: string; column: string } | null>(null)
  const [rowSaid, setRowSaid] = useState('')
  const [suggest, setSuggest] = useState<Suggestion | null>(null)

  const focusCell = (r: number, c: number) => {
    setActive([r, c])
    requestAnimationFrame(() => grid.current?.querySelector<HTMLElement>('[data-cell="' + r + ':' + c + '"]')?.focus())
  }
  const said = (row: ImportPreviewRow, err: unknown) => setRowSaid('Row ' + row.sourceRow + ': ' + errorWords(err))

  async function commit(row: ImportPreviewRow, col: GridColumn, r: number, c: number, value: string) {
    setEditing(null)
    const before = row.raw?.[col.column] ?? ''
    const hadProblem = cellView(row, col).problems.length > 0
    try {
      setRowSaid('')
      await onFix(row, col.column, value)
      if (hadProblem && before !== '' && value !== '' && !readOnly) {
        setSuggest({ column: col.column, label: col.label, from: before, to: value })
      }
    } catch (err) {
      said(row, err)
    }
    focusCell(r, c)
  }

  const tiles = filterTiles(counts)
  const empty =
    filter === 'attention' ? 'Every problem is fixed.' : filter === 'all' ? 'This file has no rows.' : 'No vendor is in this list.'

  return (
    <section className="panel imw-review" aria-labelledby="imw-check-h">
      <div className="imw-rhead">
        <h2 id="imw-check-h">Review and fix</h2>
        <p className="imw-lead">{NOTHING_SAVED}</p>
      </div>
      {skippedValues && <p className="imw-small imw-rsmall">Match values: nothing to match.</p>}

      <div className="imw-rbar">
        <span className="hm-seg imw-filter" role="group" aria-label="Show the rows">
          {tiles.map((t) => (
            <button key={t.filter} type="button" aria-pressed={filter === t.filter} onClick={() => onFilter(t.filter)}>
              {t.label} <b>{t.n.toLocaleString('en-IN')}</b>
            </button>
          ))}
        </span>
        {(counts.attention > 0 || filter === 'attention') && (
          <label className="imw-only">
            <input type="checkbox" checked={filter === 'attention'} onChange={(e) => onFilter(e.target.checked ? 'attention' : 'all')} />
            Show only problems
          </label>
        )}
        <label className="imw-dupe">
          If a vendor exists:
          <select
            value={String(Math.max(0, DUPLICATE_RULES.findIndex((d) => d.code === duplicates)))}
            disabled={busy || readOnly}
            onChange={(e) => onDuplicates(DUPLICATE_RULES[Number(e.target.value)]!.code)}
          >
            {DUPLICATE_RULES.map((d, i) => (
              <option key={i} value={String(i)}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {fixNote && (
        <p className="imw-small imw-rsmall" role="status">
          {fixNote}
        </p>
      )}
      {rowSaid && (
        <p className="imw-small imw-warn imw-rsmall" role="alert">
          {rowSaid}
        </p>
      )}

      {preview === null ? (
        <Skeleton lines={5} />
      ) : rows.length === 0 ? (
        <div className="imw-rnone">
          <span>{empty}</span>
          {filter !== 'all' && counts.total > 0 && (
            <button type="button" onClick={() => onFilter('all')}>
              Show all {counts.total.toLocaleString('en-IN')}
            </button>
          )}
        </div>
      ) : (
        <div className="imw-gwrap" role="region" aria-label="Your vendors as they will land" tabIndex={-1} aria-busy={loading || undefined}>
          <table className="imw-grid" ref={grid}>
            <caption className="vh">
              Your vendors as they will land: one row a vendor, one column a field. Choose a cell to change it.
            </caption>
            <thead>
              <tr>
                <th scope="col" className="imw-gnum">
                  Row
                </th>
                <th scope="col" className="imw-gstat">
                  Status
                </th>
                {cols.slice(0, 1).map((g) => (
                  <th key={g.column} scope="col" title={g.label + ', from your column ' + g.column}>
                    {g.label}
                  </th>
                ))}
                <th scope="col" className="imw-gprob">
                  Problem
                </th>
                {cols.slice(1).map((g) => (
                  <th key={g.column} scope="col" title={g.label + ', from your column ' + g.column}>
                    {g.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => {
                const cell = (g: GridColumn, c: number) => (
                  <GridCell
                    key={g.column}
                    row={row}
                    col={g}
                    r={r}
                    c={c}
                    active={active[0] === r && active[1] === c}
                    editing={editing?.rowId === row.id && editing.column === g.column}
                    readOnly={readOnly}
                    onStart={() => {
                      setActive([r, c])
                      setEditing({ rowId: row.id, column: g.column })
                    }}
                    onCommit={(v) => void commit(row, g, r, c, v)}
                    onCancel={() => {
                      setEditing(null)
                      focusCell(r, c)
                    }}
                    onMove={(dr, dc, e) => {
                      e.preventDefault()
                      const nr = Math.min(Math.max(r + dr, 0), rows.length - 1)
                      const nc = Math.min(Math.max(c + dc, 0), cols.length - 1)
                      focusCell(nr, nc)
                    }}
                  />
                )
                return (
                  <tr key={row.id} className={'imw-grow imw-grow-' + row.outcome.toLowerCase()}>
                    <th scope="row" className="imw-gnum">
                      {row.outcome !== 'LEAVE' && (
                        <HoldButton row={row} disabled={readOnly || busy} onHold={onHold} onError={said} />
                      )}
                      <span>{row.sourceRow}</span>
                    </th>
                    <td className="imw-gstat">
                      <Chip s={outcomeWord(row.outcome, row.outcomeLabel)} />
                    </td>
                    {cols.slice(0, 1).map((g) => cell(g, 0))}
                    <ProblemCell
                      row={row}
                      choices={choices}
                      inGrid={(column) => cols.some((g) => g.column === column)}
                      readOnly={readOnly || busy}
                      onFix={onFix}
                      onHold={onHold}
                      onEdit={(column) => {
                        const c = cols.findIndex((g) => g.column === column)
                        if (c < 0) return
                        setActive([r, c])
                        setEditing({ rowId: row.id, column })
                      }}
                      onError={said}
                    />
                    {cols.slice(1).map((g, i) => cell(g, i + 1))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <BulkFix
        columns={cols}
        suggest={suggest}
        readOnly={readOnly || busy}
        onDismiss={() => setSuggest(null)}
        onFixAll={onFixAll}
        onFindReplace={onFindReplace}
      />

      {preview !== null && total > REVIEW_PAGE && (
        <nav className="imw-pager" aria-label="Pages of rows">
          <span>{rangeWords(offset, rows.length, total)}</span>
          <span className="imw-pages">
            <button type="button" disabled={offset === 0} aria-disabled={loading || undefined} onClick={() => !loading && onPage(Math.max(0, offset - REVIEW_PAGE))}>
              Previous<span className="vh"> page</span>
            </button>
            {pageList(page, pages).map((n, i) =>
              n === '...' ? (
                <span key={'gap' + i} className="imw-gap" aria-hidden="true">
                  ...
                </span>
              ) : (
                <button
                  key={n}
                  type="button"
                  aria-current={n === page ? 'page' : undefined}
                  aria-disabled={loading || undefined}
                  onClick={() => !loading && n !== page && onPage((n - 1) * REVIEW_PAGE)}
                >
                  <span className="vh">Page </span>
                  {n}
                </button>
              ),
            )}
            <button type="button" disabled={offset + rows.length >= total} aria-disabled={loading || undefined} onClick={() => !loading && onPage(offset + REVIEW_PAGE)}>
              Next<span className="vh"> page</span>
            </button>
          </span>
        </nav>
      )}

      <ImportError error={error} />
      <div className="imw-step-foot imw-rfoot">
        <button type="button" onClick={onBack} disabled={busy}>
          Back
        </button>
        <span className="imw-rsum" aria-live="polite">
          {footerWords(counts)}
        </span>
        {draftNote && (
          <span className={'imw-draft' + (draftNote === DRAFT_SAVED ? '' : ' imw-draft-said')} role={draftNote === DRAFT_SAVED ? undefined : 'status'}>
            {draftNote}
          </span>
        )}
        <button
          type="button"
          className="primary imw-push"
          disabled={busy || ready === 0}
          aria-describedby={ready === 0 ? 'imw-review-why' : undefined}
          onClick={onNext}
        >
          Next
        </button>
        {ready === 0 && (
          <p className="imw-why-not" id="imw-review-why">
            No vendor is ready yet. Fix the rows to fix, or go back and change the matches.
          </p>
        )}
      </div>
    </section>
  )
}
